import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Storefront, Plus, MapPin, Clock, CalendarBlank, Buildings, Check, CaretUpDown, Users, Trash, SlidersHorizontal, Bell, ArrowsClockwise, BellRinging, PencilSimple, ChatCircle, CurrencyDollar, PaperPlaneTilt, CheckCircle, X, Star, Eye, Info, HandPointing, ClipboardText, ArrowRight, ShieldCheck, SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { LocationOrgPicker } from "@/components/LocationOrgPicker";

const STATUS_STYLE = {
  available: "bg-emerald-500/15 text-emerald-400",
  pending: "bg-amber-500/15 text-amber-300",
  assigned: "bg-sky-500/15 text-sky-400",
  completed: "bg-violet-500/15 text-violet-300",
  expired: "bg-secondary text-muted-foreground",
  cancelled: "bg-secondary text-muted-foreground",
};

const AVATAR_COLORS = ["bg-primary/20 text-primary", "bg-emerald-500/20 text-emerald-300", "bg-sky-500/20 text-sky-300", "bg-amber-500/20 text-amber-300", "bg-violet-500/20 text-violet-300", "bg-rose-500/20 text-rose-300"];
function avatarColor(name) {
  const s = (name || "?").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[s % AVATAR_COLORS.length];
}
function initials(name) {
  const parts = (name || "?").trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}
function fmtDate(d) {
  if (!d) return "";
  try { return format(new Date(`${d}T00:00:00`), "MMM d, yyyy (EEE)"); } catch { return d; }
}
function fmtHours(start, end, duration) {
  if (duration) return duration;
  if (start && end) {
    try {
      const [sh, sm] = start.split(":").map(Number);
      const [eh, em] = end.split(":").map(Number);
      let mins = (eh * 60 + em) - (sh * 60 + sm);
      if (mins < 0) mins += 24 * 60;
      return `${(mins / 60).toFixed(1)} hrs`;
    } catch { return ""; }
  }
  return "";
}
function fmtAgo(iso) {
  if (!iso) return "";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; }
}
function isOvertime(t) { return /overtime|premium/i.test(t || ""); }

function OrgCombobox({ value, valueName, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data: orgs = [] } = useQuery({
    queryKey: ["organizations", q],
    queryFn: async () => (await api.get(`/organizations${q ? `?q=${encodeURIComponent(q)}` : ""}`)).data,
  });
  const reqOrg = useMutation({
    mutationFn: async () => (await api.post("/organizations/request", { name: q })).data,
    onSuccess: (d) => {
      if (d.status === "exists") { onChange(d.organization); setOpen(false); toast.info("Organization already exists — selected it."); }
      else toast.success("Requested! An admin will review your organization shortly.");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" data-testid="org-combobox-trigger" className="w-full justify-between bg-secondary/50 font-normal">
          <span className="flex items-center gap-2 truncate"><Buildings className="h-4 w-4 text-muted-foreground" weight="bold" />{valueName || "Search your organization…"}</span>
          <CaretUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type your organization…" value={q} onValueChange={setQ} data-testid="org-search-input" />
          <CommandList>
            <CommandEmpty>
              <div className="py-3 px-2 text-sm">
                <p className="text-muted-foreground mb-2">No match found.</p>
                {q.trim() && (
                  <Button size="sm" variant="secondary" data-testid="org-request-btn" onClick={() => reqOrg.mutate()} disabled={reqOrg.isPending}>
                    Request "{q.trim()}"
                  </Button>
                )}
              </div>
            </CommandEmpty>
            <CommandGroup>
              {orgs.map((o) => (
                <CommandItem key={o.id} value={o.id} data-testid={`org-option-${o.id}`}
                  onSelect={() => { onChange(o); setOpen(false); }}>
                  <span className="flex-1">{o.name} <span className="text-muted-foreground text-xs">· {o.type}</span></span>
                  <Check className={`h-4 w-4 ${value === o.id ? "opacity-100" : "opacity-0"}`} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function WorkProfileGate({ meta, onDone, embedded, initial }) {
  const { setUser } = useAuth();
  const [pv, setPv] = useState(initial || {});

  const save = useMutation({
    mutationFn: async () => (await api.put("/profile/work", {
      organization_id: pv.orgId, city: pv.city || "", department_label: pv.deptLabel || "", unit: pv.unit || "",
    })).data,
    onSuccess: (u) => { setUser(u); toast.success(embedded ? "Organization updated." : "Work profile saved. Welcome to the marketplace!"); onDone?.(u); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const body = (
      <div className="space-y-4">
        <LocationOrgPicker value={pv} onChange={setPv} industryTypes={meta?.industry_types || []} departmentLabels={meta?.department_labels || []} />
        <Button onClick={() => save.mutate()} disabled={!pv.orgId || save.isPending} data-testid="profile-save" className="rounded-md font-semibold w-full">
          {save.isPending ? "Saving…" : embedded ? "Save changes" : "Save & enter marketplace"}
        </Button>
      </div>
  );
  if (embedded) return body;
  return (
    <Card className="p-6 bg-card border-border max-w-lg" data-testid="work-profile-gate">
      <h2 className="text-lg font-black mb-1">Set up your work profile</h2>
      <p className="text-sm text-muted-foreground mb-5">Verify your organization to access shifts posted by your colleagues.</p>
      {body}
    </Card>
  );
}

function PostShiftDialog({ wp, meta }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const empty = { shift_type: "", date: "", start_time: "", end_time: "", duration: "", location: "", unit: wp?.unit || "", notes: "", claim_deadline: "", visibility: "org", estimated_earnings: "", positions: 1 };
  const [f, setF] = useState(empty);

  const create = useMutation({
    mutationFn: async () => (await api.post("/shifts", { ...f, estimated_earnings: f.estimated_earnings ? parseFloat(f.estimated_earnings) : null, positions: parseInt(f.positions) || 1, claim_deadline: f.claim_deadline ? new Date(f.claim_deadline).toISOString() : null })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shifts"] }); qc.invalidateQueries({ queryKey: ["my-shifts"] });
      toast.success("Shift posted — qualified colleagues have been notified.");
      setF(empty); setOpen(false);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="post-shift-btn" className="rounded-md font-semibold"><Plus className="h-4 w-4 mr-1.5" weight="bold" /> Post Available Shift</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="post-shift-dialog">
        <DialogHeader><DialogTitle>Post an available shift</DialogTitle></DialogHeader>
        <div className="rounded-md bg-secondary/40 p-3 text-sm mb-1">
          <p className="font-semibold">{wp.organization_name}</p>
          <p className="text-muted-foreground text-xs">{wp.profession}{wp.unit ? ` · ${wp.unit}` : ""}</p>
        </div>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Shift type</Label>
              <Select value={f.shift_type} onValueChange={(v) => setF({ ...f, shift_type: v })}>
                <SelectTrigger data-testid="shift-type" className="mt-1.5 bg-secondary/50"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(meta?.shift_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" data-testid="shift-date" className="mt-1.5 bg-secondary/50" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><Label>Start</Label><Input type="time" data-testid="shift-start" className="mt-1.5 bg-secondary/50" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} /></div>
            <div><Label>End</Label><Input type="time" data-testid="shift-end" className="mt-1.5 bg-secondary/50" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} /></div>
            <div><Label>Duration</Label><Input data-testid="shift-duration" className="mt-1.5 bg-secondary/50" value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} placeholder="12 hours" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Division / Department</Label><Input data-testid="shift-unit" className="mt-1.5 bg-secondary/50" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder={wp.unit || "Optional"} /></div>
            <div><Label>Location</Label><Input data-testid="shift-location" className="mt-1.5 bg-secondary/50" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="Optional" /></div>
          </div>
          <div><Label>Notes</Label><Textarea data-testid="shift-notes" className="mt-1.5 bg-secondary/50" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Need one qualified officer…" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Estimated earnings ($)</Label><Input type="number" step="0.01" data-testid="shift-earnings" className="mt-1.5 bg-secondary/50" value={f.estimated_earnings} onChange={(e) => setF({ ...f, estimated_earnings: e.target.value })} placeholder="Optional" /></div>
            <div><Label>Positions available</Label><Input type="number" min="1" data-testid="shift-positions" className="mt-1.5 bg-secondary/50" value={f.positions} onChange={(e) => setF({ ...f, positions: e.target.value })} /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Claim deadline</Label><Input type="datetime-local" data-testid="shift-deadline" className="mt-1.5 bg-secondary/50" value={f.claim_deadline} onChange={(e) => setF({ ...f, claim_deadline: e.target.value })} /></div>
            <div>
              <Label>Visible to</Label>
              <Select value={f.visibility} onValueChange={(v) => setF({ ...f, visibility: v })}>
                <SelectTrigger data-testid="shift-visibility" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="org">Whole organization</SelectItem>
                  <SelectItem value="unit">My division/department only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => create.mutate()} disabled={!f.shift_type || !f.date || create.isPending} data-testid="shift-submit" className="rounded-md font-semibold">
            {create.isPending ? "Posting…" : "Post shift"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChatDialog({ shiftId, otherId, triggerLabel, testid }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const { data } = useQuery({
    queryKey: ["chat", shiftId, otherId],
    queryFn: async () => (await api.get(`/shifts/${shiftId}/chat/${otherId}/messages`)).data,
    enabled: open, refetchInterval: open ? 6000 : false,
  });
  const send = useMutation({
    mutationFn: async () => api.post(`/shifts/${shiftId}/chat/${otherId}/messages`, { body: text }),
    onSuccess: () => { setText(""); qc.invalidateQueries({ queryKey: ["chat", shiftId, otherId] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const msgs = data?.messages || [];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-md" data-testid={testid}><ChatCircle className="h-4 w-4 mr-1.5" weight="bold" /> {triggerLabel || "Message"}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid={`chat-dialog-${shiftId}`}>
        <DialogHeader><DialogTitle>{data?.other_name ? `Chat with ${data.other_name}` : "Chat"}</DialogTitle></DialogHeader>
        <div className="h-72 overflow-y-auto space-y-2 pr-1 bg-secondary/20 rounded-md p-3" data-testid="chat-messages">
          {msgs.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No messages yet. Say hello 👋</p>}
          {msgs.map((m) => (
            <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-1.5 ${m.mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                <p className="text-sm">{m.body}</p>
                <p className={`text-[10px] mt-0.5 ${m.mine ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                  {m.created_at ? format(new Date(m.created_at), "MMM d, h:mm a") : ""}{m.mine ? (m.read ? " · Read" : " · Sent") : ""}
                </p>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) send.mutate(); }} className="flex gap-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="bg-secondary/50" data-testid="chat-input" />
          <Button type="submit" disabled={!text.trim() || send.isPending} data-testid="chat-send" className="rounded-md"><PaperPlaneTilt className="h-4 w-4" weight="bold" /></Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StarPicker({ value, onChange, testidPrefix }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testidPrefix}-star-${n}`} className="transition-transform duration-150 hover:scale-110">
          <Star className={`h-6 w-6 ${n <= value ? "text-amber-400" : "text-muted-foreground"}`} weight={n <= value ? "fill" : "regular"} />
        </button>
      ))}
    </div>
  );
}

function RateDialog({ shiftId, triggerLabel, testid }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({});
  const { data } = useQuery({ queryKey: ["rate-targets", shiftId], queryFn: async () => (await api.get(`/shifts/${shiftId}/rate-targets`)).data, enabled: open });
  const targets = data?.targets || [];
  const rate = useMutation({
    mutationFn: async ({ toId, stars, comment }) => api.post(`/shifts/${shiftId}/rate`, { to_id: toId, stars, comment, would_again: stars >= 3 }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rate-targets", shiftId] }); qc.invalidateQueries({ queryKey: ["shift-requests", shiftId] }); toast.success("Rating submitted. Thanks!"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-md" data-testid={testid}><Star className="h-4 w-4 mr-1.5" weight="fill" /> {triggerLabel || "Rate"}</Button></DialogTrigger>
      <DialogContent data-testid={`rate-dialog-${shiftId}`}>
        <DialogHeader><DialogTitle>Rate this shift</DialogTitle><DialogDescription>Rate the other party from this completed shift. Ratings build reputation and badges.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {targets.length === 0 && <p className="text-sm text-muted-foreground">Nothing to rate here.</p>}
          {targets.map((t) => (
            <div key={t.id} className="rounded-md bg-secondary/40 p-3" data-testid={`rate-target-${t.id}`}>
              <p className="text-sm font-semibold mb-2">{t.name}</p>
              {t.rated ? <p className="text-xs text-emerald-400">✓ You already rated them.</p> : (
                <>
                  <StarPicker value={state[t.id]?.stars || 0} onChange={(n) => setState({ ...state, [t.id]: { ...state[t.id], stars: n } })} testidPrefix={`rate-${t.id}`} />
                  <Textarea className="mt-2 bg-secondary/50" rows={2} placeholder="Optional comment (showed up? communication?)" data-testid={`rate-comment-${t.id}`}
                    value={state[t.id]?.comment || ""} onChange={(e) => setState({ ...state, [t.id]: { ...state[t.id], comment: e.target.value } })} />
                  <Button size="sm" className="rounded-md mt-2 font-semibold" disabled={!state[t.id]?.stars || rate.isPending} data-testid={`rate-submit-${t.id}`}
                    onClick={() => rate.mutate({ toId: t.id, stars: state[t.id].stars, comment: state[t.id]?.comment || "" })}>Submit rating</Button>
                </>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RepBadges({ rating, ratingCount, badges }) {
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {rating != null && <span className="inline-flex items-center gap-0.5 text-amber-400 text-xs font-semibold"><Star className="h-3 w-3" weight="fill" />{rating}{ratingCount ? ` (${ratingCount})` : ""}</span>}
      {(badges || []).map((b) => <span key={b} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">{b}</span>)}
    </span>
  );
}

function ShiftCard({ s }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: myReq } = useQuery({
    queryKey: ["my-request", s.id],
    queryFn: async () => (await api.get(`/shifts/${s.id}/my-request`)).data,
    enabled: !s.is_mine,
  });
  const request = useMutation({
    mutationFn: async () => api.post(`/shifts/${s.id}/request`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shifts"] });
      qc.invalidateQueries({ queryKey: ["my-request", s.id] });
      qc.invalidateQueries({ queryKey: ["my-requests"] });
      toast.success("Shift claimed — pending the poster's approval.");
      setOpen(false);
    },
    onError: (e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); },
  });
  const reqStatus = myReq?.status;
  const ot = isOvertime(s.shift_type);
  const badgeText = s.status === "assigned" ? "ASSIGNED" : ot ? "OVERTIME AVAILABLE" : "SHIFT AVAILABLE";
  const badgeStyle = s.status === "assigned" ? STATUS_STYLE.assigned : ot ? "bg-violet-500/15 text-violet-300" : "bg-emerald-500/15 text-emerald-400";

  const claimBlock = () => {
    if (s.is_mine) return <p className="text-xs text-muted-foreground text-center">This is your listing.</p>;
    if (reqStatus === "confirmed") return <div className="rounded-md bg-sky-500/15 text-sky-300 text-sm font-semibold py-2.5 text-center" data-testid={`claim-confirmed-${s.id}`}>You're assigned 🎉</div>;
    if (reqStatus === "pending") return <div className="rounded-md bg-amber-500/15 text-amber-300 text-sm font-semibold py-2.5 text-center" data-testid={`claim-pending-${s.id}`}>Pending Approval</div>;
    if (reqStatus === "rejected") return <div className="rounded-md bg-secondary text-muted-foreground text-sm py-2.5 text-center">Not selected</div>;
    if (s.status === "assigned") return <div className="rounded-md bg-secondary text-muted-foreground text-sm py-2.5 text-center">Already assigned</div>;
    if (s.can_claim === false) return <div className="rounded-md bg-amber-500/10 text-amber-300 text-sm py-2.5 text-center" data-testid={`claim-locked-${s.id}`}>Join this organization to claim</div>;
    return (
      <div className="flex gap-2">
        <Button onClick={() => request.mutate()} disabled={request.isPending} data-testid={`claim-shift-btn-${s.id}`} className="flex-1 rounded-md font-semibold"><HandPointing className="h-4 w-4 mr-1.5" weight="bold" /> {request.isPending ? "Claiming…" : "Claim Shift"}</Button>
        <Button variant="outline" onClick={() => setOpen(false)} data-testid={`claim-cancel-${s.id}`} className="rounded-md">Cancel</Button>
      </div>
    );
  };

  return (
    <Card className="p-5 bg-card border-border flex flex-col transition-all duration-200 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5" data-testid={`shift-card-${s.id}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <Badge className={`${badgeStyle} border-0 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1`} data-testid={`shift-status-${s.id}`}>{badgeText}</Badge>
        {s.estimated_earnings != null && <span className="text-sm font-black text-emerald-400 flex items-center gap-0.5" data-testid={`earnings-${s.id}`}><CurrencyDollar className="h-4 w-4" weight="bold" />{s.estimated_earnings}</span>}
      </div>
      <h3 className="text-xl font-black tracking-tight">{s.shift_type}</h3>
      <p className="text-sm text-muted-foreground leading-snug mt-0.5">{s.organization_name}{s.unit ? <><br />{s.unit}</> : ""}</p>

      <div className="space-y-2 text-sm text-muted-foreground mt-4">
        <span className="flex items-center gap-2"><CalendarBlank className="h-4 w-4 shrink-0" weight="bold" /> {fmtDate(s.date)}</span>
        <span className="flex items-center gap-2"><Clock className="h-4 w-4 shrink-0" weight="bold" /> {s.start_time}{s.end_time ? ` – ${s.end_time}` : ""}{fmtHours(s.start_time, s.end_time, s.duration) ? ` (${fmtHours(s.start_time, s.end_time, s.duration)})` : ""}</span>
        {(s.location || s.unit) && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 shrink-0" weight="bold" /> {s.location || s.unit}</span>}
        {s.positions > 1 && <span className="flex items-center gap-2"><Users className="h-4 w-4 shrink-0" weight="bold" /> {s.filled || 0}/{s.positions} positions filled</span>}
      </div>

      <div className="border-t border-border mt-4 pt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 font-bold text-xs ${avatarColor(s.poster_name)}`}>{initials(s.poster_name)}</div>
          <div className="min-w-0">
            <p className="text-[11px] text-muted-foreground leading-none">Posted by</p>
            <p className="text-sm font-semibold truncate">{s.poster_name}</p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground shrink-0">{fmtAgo(s.created_at)}</span>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="w-full rounded-md mt-4 font-semibold" data-testid={`view-shift-${s.id}`}><Eye className="h-4 w-4 mr-1.5" weight="bold" /> View Shift</Button>
        </DialogTrigger>
        <DialogContent data-testid={`shift-detail-${s.id}`}>
          <DialogHeader>
            <DialogTitle>{s.is_mine || reqStatus ? "Shift details" : "Do you want to claim this shift?"}</DialogTitle>
            <DialogDescription>{s.shift_type} · {s.organization_name}{s.unit ? ` · ${s.unit}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2"><CalendarBlank className="h-4 w-4 text-muted-foreground" weight="bold" /> {fmtDate(s.date)}</div>
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-muted-foreground" weight="bold" /> {s.start_time}{s.end_time ? ` – ${s.end_time}` : ""}{fmtHours(s.start_time, s.end_time, s.duration) ? ` (${fmtHours(s.start_time, s.end_time, s.duration)})` : ""}</div>
            {s.location && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" weight="bold" /> {s.location}</div>}
            {s.estimated_earnings != null && <div className="flex items-center gap-2 text-emerald-400 font-semibold"><CurrencyDollar className="h-4 w-4" weight="bold" /> Est. earnings ${s.estimated_earnings}</div>}
            <div className="flex items-center gap-2 text-muted-foreground"><Users className="h-4 w-4" weight="bold" /> Posted by {s.poster_name}</div>
            {s.notes && <p className="bg-secondary/40 rounded-md px-3 py-2 mt-1">{s.notes}</p>}
          </div>
          {!s.is_mine && !reqStatus && s.status !== "assigned" && (
            <p className="text-xs text-muted-foreground bg-secondary/40 rounded-md px-3 py-2">The poster reviews all claimants and approves one. You'll be notified when approved.</p>
          )}
          <div className="pt-1">{claimBlock()}</div>
          {!s.is_mine && (
            <div className="pt-1"><ChatDialog shiftId={s.id} otherId={s.poster_id} triggerLabel="Message the poster" testid={`chat-btn-${s.id}`} /></div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MyListingCard({ s }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: reqs = [] } = useQuery({
    queryKey: ["shift-requests", s.id],
    queryFn: async () => (await api.get(`/shifts/${s.id}/requests`)).data,
    enabled: open, refetchInterval: open ? 8000 : false,
  });
  const act = useMutation({
    mutationFn: async ({ reqId, action }) => api.post(`/shifts/${s.id}/requests/${reqId}/${action}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["shift-requests", s.id] }); qc.invalidateQueries({ queryKey: ["my-shifts"] }); toast.success("Updated."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const cancel = useMutation({
    mutationFn: async () => api.delete(`/shifts/${s.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-shifts"] }); toast.success("Listing cancelled."); },
  });
  const complete = useMutation({
    mutationFn: async () => api.post(`/shifts/${s.id}/complete`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-shifts"] }); toast.success("Marked complete — you can now rate."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const badge = s.status === "assigned" ? "Assigned" : s.status === "available" ? `${s.interested || 0} Interested` : s.status;

  return (
    <Card className="p-5 bg-card border-border" data-testid={`my-shift-${s.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold">{s.shift_type}</span>
            <Badge className={`${s.status === "available" ? "bg-primary/15 text-primary" : STATUS_STYLE[s.status]} border-0 text-[10px] uppercase`}>{badge}</Badge>
            {s.estimated_earnings != null && <span className="text-xs font-bold text-emerald-400">${s.estimated_earnings}</span>}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{s.date} · {s.start_time}{s.end_time ? `–${s.end_time}` : ""}{s.unit ? ` · ${s.unit}` : ""}{s.positions > 1 ? ` · ${s.filled || 0}/${s.positions} filled` : ""}</p>
        </div>
        {["available"].includes(s.status) && (
          <button onClick={() => cancel.mutate()} data-testid={`cancel-shift-${s.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash className="h-4 w-4" weight="bold" /></button>
        )}
      </div>
      {s.status === "assigned" && <p className="text-sm text-sky-400 mt-2">Assigned to <span className="font-semibold">{(s.assignees || []).map((a) => a.name).join(", ") || s.assigned_to_name}</span></p>}
      <div className="flex flex-wrap gap-2 mt-3">
        {s.status === "assigned" && (
          <Button size="sm" onClick={() => complete.mutate()} disabled={complete.isPending} data-testid={`complete-shift-${s.id}`} className="rounded-md bg-emerald-600 hover:bg-emerald-500">
            <CheckCircle className="h-4 w-4 mr-1.5" weight="bold" /> Mark complete
          </Button>
        )}
        {s.status === "completed" && <RateDialog shiftId={s.id} triggerLabel="Rate the employee" testid={`rate-btn-${s.id}`} />}
      </div>
      {["available", "assigned"].includes(s.status) && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-md mt-3" data-testid={`view-requests-${s.id}`}>
              <Users className="h-4 w-4 mr-1.5" weight="bold" /> Applicants ({s.request_count})
            </Button>
          </DialogTrigger>
          <DialogContent data-testid={`requests-dialog-${s.id}`}>
            <DialogHeader><DialogTitle>Applicants for this shift</DialogTitle></DialogHeader>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {reqs.length === 0 && <p className="text-sm text-muted-foreground">No applicants yet.</p>}
              {reqs.map((r) => (
                <div key={r.id} className="rounded-md bg-secondary/40 px-3 py-2.5" data-testid={`request-row-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0 font-bold text-primary text-sm">{(r.requester_name || "?").charAt(0).toUpperCase()}</div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{r.requester_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{r.requester_profession || "—"}{r.requester_unit ? ` · ${r.requester_unit}` : ""} · {r.completed} done</p>
                        <div className="mt-0.5"><RepBadges rating={r.rating} ratingCount={r.rating_count} badges={r.badges} /></div>
                      </div>
                    </div>
                    <Badge className="bg-secondary text-muted-foreground border-0 text-[10px] capitalize shrink-0">{r.status}</Badge>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {["pending", "selected"].includes(r.status) && s.status !== "assigned" && (
                      <>
                        <Button size="sm" onClick={() => act.mutate({ reqId: r.id, action: "approve" })} data-testid={`approve-${r.id}`} className="rounded-md bg-emerald-600 hover:bg-emerald-500"><CheckCircle className="h-4 w-4 mr-1.5" weight="bold" /> Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => act.mutate({ reqId: r.id, action: "reject" })} data-testid={`reject-${r.id}`} className="rounded-md">Decline</Button>
                      </>
                    )}
                    {r.status === "confirmed" && <span className="text-xs text-emerald-400 self-center font-semibold">Approved ✓</span>}
                    <ChatDialog shiftId={s.id} otherId={r.requester_id} testid={`chat-applicant-${r.id}`} />
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

function PrefsDialog({ prefs, meta }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(prefs);
  const save = useMutation({
    mutationFn: async () => (await api.put("/profile/marketplace-prefs", p)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["work-profile"] }); toast.success("Notification preferences saved."); setOpen(false); },
  });
  const toggleType = (t) => setP({ ...p, shift_types: p.shift_types?.includes(t) ? p.shift_types.filter((x) => x !== t) : [...(p.shift_types || []), t] });
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setP(prefs); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-md" data-testid="prefs-btn"><SlidersHorizontal className="h-4 w-4 mr-1.5" weight="bold" /> Notifications</Button>
      </DialogTrigger>
      <DialogContent data-testid="prefs-dialog">
        <DialogHeader><DialogTitle>Notification preferences</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2.5">
            <span className="text-sm">In-app notifications</span>
            <Switch checked={!!p.notify_inapp} onCheckedChange={(v) => setP({ ...p, notify_inapp: v })} data-testid="pref-inapp" />
          </div>
          <div className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2.5">
            <span className="text-sm">Email notifications</span>
            <div className="w-40">
              <Select value={p.email_frequency || "off"} onValueChange={(v) => setP({ ...p, email_frequency: v })}>
                <SelectTrigger data-testid="pref-email-frequency" className="bg-secondary/60 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="daily">Daily digest</SelectItem>
                  <SelectItem value="weekly">Weekly digest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Only notify me about these shift types</p>
            <p className="text-xs text-muted-foreground mb-2">Leave all off to get notified about every shift.</p>
            <div className="flex flex-wrap gap-2">
              {(meta?.shift_types || []).map((t) => (
                <button key={t} onClick={() => toggleType(t)} data-testid={`pref-type-${t}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${p.shift_types?.includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="prefs-save" className="rounded-md font-semibold">Save preferences</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PostSwapDialog({ wp, meta }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const empty = { shift_type: "", date: "", start_time: "", end_time: "", unit: wp?.unit || "", notes: "",
                  wanted_shift_type: "", wanted_date: "", wanted_day_part: "any", wanted_notes: "", visibility: "org" };
  const [f, setF] = useState(empty);
  const create = useMutation({
    mutationFn: async () => (await api.post("/swaps", f)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["swaps"] }); qc.invalidateQueries({ queryKey: ["my-shifts"] }); toast.success("Swap posted — colleagues notified."); setF(empty); setOpen(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-md font-semibold" data-testid="post-swap-btn"><ArrowsClockwise className="h-4 w-4 mr-1.5" weight="bold" /> Offer a Swap</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="post-swap-dialog">
        <DialogHeader><DialogTitle>Offer a shift swap</DialogTitle></DialogHeader>
        <p className="text-sm font-semibold text-muted-foreground">The shift you have</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Shift type</Label>
            <Select value={f.shift_type} onValueChange={(v) => setF({ ...f, shift_type: v })}>
              <SelectTrigger data-testid="swap-shift-type" className="mt-1.5 bg-secondary/50"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(meta?.shift_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Date</Label><Input type="date" data-testid="swap-date" className="mt-1.5 bg-secondary/50" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          <div><Label>Start</Label><Input type="time" data-testid="swap-start" className="mt-1.5 bg-secondary/50" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} /></div>
          <div><Label>End</Label><Input type="time" data-testid="swap-end" className="mt-1.5 bg-secondary/50" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} /></div>
        </div>
        <p className="text-sm font-semibold text-muted-foreground mt-2">What you want in return</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Desired type</Label>
            <Select value={f.wanted_shift_type} onValueChange={(v) => setF({ ...f, wanted_shift_type: v })}>
              <SelectTrigger data-testid="swap-wanted-type" className="mt-1.5 bg-secondary/50"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>{(meta?.shift_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Desired day</Label>
            <Select value={f.wanted_day_part} onValueChange={(v) => setF({ ...f, wanted_day_part: v })}>
              <SelectTrigger data-testid="swap-wanted-daypart" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="day">Day</SelectItem><SelectItem value="night">Night</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Notes</Label><Textarea data-testid="swap-notes" className="mt-1.5 bg-secondary/50" rows={2} value={f.wanted_notes} onChange={(e) => setF({ ...f, wanted_notes: e.target.value })} placeholder="Prefer Sunday day shift…" /></div>
        <DialogFooter><Button onClick={() => create.mutate()} disabled={!f.shift_type || !f.date || create.isPending} data-testid="swap-submit" className="rounded-md font-semibold">Post swap</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SwapCard({ s }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [o, setO] = useState({ offered_shift_type: "", offered_date: "", offered_start: "", offered_end: "", message: "" });
  const { data: myOffer } = useQuery({ queryKey: ["my-offer", s.id], queryFn: async () => (await api.get(`/swaps/${s.id}/my-offer`)).data, enabled: !s.is_mine });
  const offer = useMutation({
    mutationFn: async () => api.post(`/swaps/${s.id}/offer`, o),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["swaps"] }); qc.invalidateQueries({ queryKey: ["my-offer", s.id] }); toast.success("Swap offer sent."); setOpen(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const st = myOffer?.status;
  const w = s.wanted || {};
  return (
    <Card className="p-5 bg-card border-border" data-testid={`swap-card-${s.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <ArrowsClockwise className="h-4 w-4 text-primary" weight="bold" />
        <span className="font-bold">{s.shift_type}</span>
        <Badge className={`${STATUS_STYLE[s.status]} border-0 text-[10px] uppercase`}>{s.status}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">Has: <span className="text-foreground">{s.date} {s.start_time}{s.end_time ? `–${s.end_time}` : ""}</span></p>
      <p className="text-sm text-muted-foreground">Wants: <span className="text-foreground">{w.shift_type || "Any"}{w.day_part && w.day_part !== "any" ? ` · ${w.day_part}` : ""}{w.date ? ` · ${w.date}` : ""}</span></p>
      {w.notes && <p className="text-sm bg-secondary/40 rounded-md px-3 py-2 mt-2">{w.notes}</p>}
      {!s.is_mine && (
        s.can_claim === false ? <div className="rounded-md bg-amber-500/10 text-amber-300 text-sm py-2.5 text-center mt-3" data-testid={`swap-locked-${s.id}`}>Join this organization to offer a swap</div>
        : st === "pending" ? <Button disabled variant="secondary" className="w-full rounded-md mt-3" data-testid={`swap-offered-${s.id}`}>Offer sent</Button>
        : st === "accepted" ? <Button disabled className="w-full rounded-md mt-3 bg-sky-600">Swap confirmed 🔄</Button>
        : st === "rejected" ? <Button disabled variant="secondary" className="w-full rounded-md mt-3">Offer declined</Button>
        : (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="w-full rounded-md mt-3 font-semibold" data-testid={`swap-offer-btn-${s.id}`}>Offer a Swap</Button></DialogTrigger>
            <DialogContent data-testid={`swap-offer-dialog-${s.id}`}>
              <DialogHeader><DialogTitle>Offer your shift in exchange</DialogTitle></DialogHeader>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Your shift type</Label>
                  <Input data-testid="offer-type" className="mt-1.5 bg-secondary/50" value={o.offered_shift_type} onChange={(e) => setO({ ...o, offered_shift_type: e.target.value })} placeholder="Regular Shift" />
                </div>
                <div><Label>Date</Label><Input type="date" data-testid="offer-date" className="mt-1.5 bg-secondary/50" value={o.offered_date} onChange={(e) => setO({ ...o, offered_date: e.target.value })} /></div>
                <div><Label>Start</Label><Input type="time" data-testid="offer-start" className="mt-1.5 bg-secondary/50" value={o.offered_start} onChange={(e) => setO({ ...o, offered_start: e.target.value })} /></div>
                <div><Label>End</Label><Input type="time" data-testid="offer-end" className="mt-1.5 bg-secondary/50" value={o.offered_end} onChange={(e) => setO({ ...o, offered_end: e.target.value })} /></div>
              </div>
              <div><Label>Message</Label><Textarea data-testid="offer-message" className="mt-1.5 bg-secondary/50" rows={2} value={o.message} onChange={(e) => setO({ ...o, message: e.target.value })} /></div>
              <DialogFooter><Button onClick={() => offer.mutate()} disabled={!o.offered_shift_type || !o.offered_date || offer.isPending} data-testid="offer-submit" className="rounded-md font-semibold">Send offer</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )
      )}
    </Card>
  );
}

function SwapListingCard({ s }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: offers = [] } = useQuery({ queryKey: ["swap-offers", s.id], queryFn: async () => (await api.get(`/swaps/${s.id}/offers`)).data, enabled: open });
  const act = useMutation({
    mutationFn: async ({ id, action }) => api.post(`/swaps/${s.id}/offers/${id}/${action}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["swap-offers", s.id] }); qc.invalidateQueries({ queryKey: ["my-shifts"] }); toast.success("Updated."); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  return (
    <Card className="p-5 bg-card border-border" data-testid={`my-swap-${s.id}`}>
      <div className="flex items-center gap-2">
        <ArrowsClockwise className="h-4 w-4 text-primary" weight="bold" />
        <span className="font-bold">{s.shift_type}</span>
        <Badge className={`${STATUS_STYLE[s.status]} border-0 text-[10px] uppercase`}>{s.status}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{s.date} {s.start_time}{s.end_time ? `–${s.end_time}` : ""} · wants {(s.wanted || {}).shift_type || "any"}</p>
      {s.status === "assigned" && <p className="text-sm text-sky-400 mt-2">Swapped with <span className="font-semibold">{s.assigned_to_name}</span></p>}
      {["available", "pending"].includes(s.status) && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button variant="outline" size="sm" className="rounded-md mt-3" data-testid={`view-offers-${s.id}`}><Users className="h-4 w-4 mr-1.5" weight="bold" /> View offers ({s.request_count})</Button></DialogTrigger>
          <DialogContent data-testid={`offers-dialog-${s.id}`}>
            <DialogHeader><DialogTitle>Swap offers</DialogTitle></DialogHeader>
            <div className="space-y-2">
              {offers.length === 0 && <p className="text-sm text-muted-foreground">No offers yet.</p>}
              {offers.map((of) => (
                <div key={of.id} className="rounded-md bg-secondary/40 px-3 py-2" data-testid={`offer-row-${of.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold">{of.offerer_name}</p>
                      <p className="text-xs text-muted-foreground">Offers {of.offered?.shift_type} on {of.offered?.date} · {of.status}</p>
                      {of.message && <p className="text-xs text-muted-foreground mt-0.5">"{of.message}"</p>}
                    </div>
                    {of.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => act.mutate({ id: of.id, action: "accept" })} data-testid={`swap-accept-${of.id}`} className="rounded-md">Accept</Button>
                        <Button size="sm" variant="outline" onClick={() => act.mutate({ id: of.id, action: "reject" })} data-testid={`swap-reject-${of.id}`} className="rounded-md">Reject</Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

const EMPTY_ALERT = { name: "", shift_types: [], units: [], day_part: "any", weekend_only: false, active: true };

function AlertsDialog({ meta, wp }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ALERT);
  const { data: alerts = [] } = useQuery({ queryKey: ["alerts"], queryFn: async () => (await api.get("/alerts")).data, enabled: open });
  const save = useMutation({
    mutationFn: async () => (await api.post("/alerts", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts"] }); toast.success("Alert saved."); setForm(EMPTY_ALERT); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/alerts/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts"] }); toast.success("Alert removed."); },
  });
  const toggleType = (t) => setForm({ ...form, shift_types: form.shift_types.includes(t) ? form.shift_types.filter((x) => x !== t) : [...form.shift_types, t] });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-md" data-testid="alerts-btn"><BellRinging className="h-4 w-4 mr-1.5" weight="bold" /> Saved Alerts</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="alerts-dialog">
        <DialogHeader><DialogTitle>Saved smart alerts</DialogTitle></DialogHeader>
        <div className="space-y-2" data-testid="alerts-list">
          {alerts.length === 0 && <p className="text-sm text-muted-foreground">No saved alerts yet. Create one below.</p>}
          {alerts.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2" data-testid={`alert-${a.id}`}>
              <div>
                <p className="text-sm font-semibold">{a.name}</p>
                <p className="text-xs text-muted-foreground">{(a.shift_types?.length ? a.shift_types.join(", ") : "Any type")}{a.weekend_only ? " · weekends" : ""}{a.day_part !== "any" ? ` · ${a.day_part}` : ""}</p>
              </div>
              <button onClick={() => del.mutate(a.id)} data-testid={`alert-del-${a.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash className="h-4 w-4" weight="bold" /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-4 space-y-3">
          <div><Label>Alert name</Label><Input data-testid="alert-name" className="mt-1.5 bg-secondary/50" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Weekend Callbacks" /></div>
          <div>
            <p className="text-sm mb-1.5">Shift types (leave empty for any)</p>
            <div className="flex flex-wrap gap-2">
              {(meta?.shift_types || []).map((t) => (
                <button key={t} onClick={() => toggleType(t)} data-testid={`alert-type-${t}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${form.shift_types.includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Day / night</Label>
              <Select value={form.day_part} onValueChange={(v) => setForm({ ...form, day_part: v })}>
                <SelectTrigger data-testid="alert-daypart" className="mt-1.5 bg-secondary/50"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="day">Day</SelectItem><SelectItem value="night">Night</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="flex items-center justify-between rounded-md bg-secondary/40 px-3 py-2.5 w-full">
                <span className="text-sm">Weekends only</span>
                <Switch checked={form.weekend_only} onCheckedChange={(v) => setForm({ ...form, weekend_only: v })} data-testid="alert-weekend" />
              </div>
            </div>
          </div>
          <Button onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="alert-save" className="rounded-md font-semibold w-full">
            <Plus className="h-4 w-4 mr-1.5" weight="bold" /> Add alert
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function MyRequestCard({ s }) {
  const st = s.my_request_status;
  const label = st === "confirmed" ? "Claimed" : st === "pending" ? "Pending Approval" : st === "rejected" ? "Not selected" : st;
  const style = st === "confirmed" ? "bg-sky-500/15 text-sky-300" : st === "pending" ? "bg-amber-500/15 text-amber-300" : "bg-secondary text-muted-foreground";
  return (
    <Card className="p-5 bg-card border-border" data-testid={`my-request-${s.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold">{s.shift_type}</span>
            <Badge className={`${style} border-0 text-[10px] uppercase font-bold`} data-testid={`my-request-status-${s.id}`}>{label}</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{s.organization_name}{s.unit ? ` · ${s.unit}` : ""}</p>
        </div>
        {s.estimated_earnings != null && <span className="text-sm font-black text-emerald-400 shrink-0">${s.estimated_earnings}</span>}
      </div>
      <div className="space-y-1.5 text-sm text-muted-foreground mt-3">
        <span className="flex items-center gap-2"><CalendarBlank className="h-4 w-4 shrink-0" weight="bold" /> {fmtDate(s.date)}</span>
        <span className="flex items-center gap-2"><Clock className="h-4 w-4 shrink-0" weight="bold" /> {s.start_time}{s.end_time ? ` – ${s.end_time}` : ""}</span>
        <span className="flex items-center gap-2"><Users className="h-4 w-4 shrink-0" weight="bold" /> Posted by {s.poster_name}</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <ChatDialog shiftId={s.id} otherId={s.poster_id} triggerLabel="Message poster" testid={`my-request-chat-${s.id}`} />
        {s.status === "completed" && <RateDialog shiftId={s.id} triggerLabel="Rate the poster" testid={`my-request-rate-${s.id}`} />}
      </div>
    </Card>
  );
}

function HowItWorks() {
  const steps = [
    { icon: Eye, title: "View Available Shifts", body: "Browse shifts posted by verified colleagues." },
    { icon: HandPointing, title: "Claim a Shift", body: "Click a shift and confirm you want it." },
    { icon: ClipboardText, title: "Pending Approval", body: "The original poster is notified to approve." },
    { icon: CheckCircle, title: "Shift Assigned", body: "Once approved, the shift is yours!" },
  ];
  return (
    <Card className="p-6 bg-card border-border mt-6" data-testid="how-it-works">
      <p className="font-bold mb-4">How it works</p>
      <div className="grid gap-4 md:grid-cols-4">
        {steps.map((st, i) => (
          <div key={st.title} className="flex items-start gap-3 relative">
            <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0"><st.icon className="h-5 w-5 text-primary" weight="bold" /></div>
            <div>
              <p className="text-sm font-semibold">{i + 1}. {st.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{st.body}</p>
            </div>
            {i < steps.length - 1 && <ArrowRight className="h-4 w-4 text-muted-foreground absolute -right-2 top-2.5 hidden md:block" weight="bold" />}
          </div>
        ))}
      </div>
    </Card>
  );
}

function ChangeProfileDialog({ meta, initial, trigger, title = "Update your work profile", onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <button className="text-xs text-primary hover:underline ml-1" data-testid="change-profile-link">Change</button>}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="change-profile-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Set your country, state/province, organization, profession and division.</DialogDescription></DialogHeader>
        <WorkProfileGate meta={meta} initial={initial} onDone={(u) => { setOpen(false); onDone?.(u); }} embedded />
      </DialogContent>
    </Dialog>
  );
}


function BrowseOrgDialog({ meta, onSelect, trigger }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [industry, setIndustry] = useState("");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newWebsite, setNewWebsite] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [creating, setCreating] = useState(false);
  const { data: countries = [] } = useQuery({ queryKey: ["geo-countries"], queryFn: async () => (await api.get("/geo/countries")).data, enabled: open });
  const { data: states = [] } = useQuery({ queryKey: ["geo-states", country], queryFn: async () => (await api.get(`/geo/states?country=${encodeURIComponent(country)}`)).data, enabled: open && !!country });
  const orgParams = new URLSearchParams();
  if (country) orgParams.set("country", country);
  if (state) orgParams.set("state", state);
  if (city) orgParams.set("city", city);
  if (industry) orgParams.set("industry", industry);
  if (q) orgParams.set("q", q);
  const { data: orgs = [] } = useQuery({
    queryKey: ["browse-orgs", country, state, city, industry, q],
    queryFn: async () => (await api.get(`/organizations?${orgParams.toString()}`)).data,
    enabled: open && !!country,
  });

  const createOrg = async () => {
    const name = (newName || q).trim();
    if (!name) { toast.error("Enter your organization's name."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/organizations/request", {
        name, type: industry || "Other", country, state, city, website: newWebsite.trim(), email_domain: newDomain.trim(),
      });
      const org = data.organization;
      if (data.status === "exists") toast.info("That organization already exists — selecting it.");
      else if (data.verified) toast.success("Added & verified instantly ✓");
      else toast.success("Added — an admin will verify it shortly. You can use it now.");
      qc.invalidateQueries({ queryKey: ["browse-orgs"] });
      onSelect(org);
      setOpen(false);
      setShowAdd(false); setNewName(""); setNewWebsite(""); setNewDomain("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setCreating(false);
  };

  const addForm = () => (
    <div className="rounded-md bg-secondary/40 border border-border p-3 space-y-2" data-testid="browse-add-form">
      <p className="text-sm font-semibold">Add your organization</p>
      <p className="text-xs text-muted-foreground">{[industry, city, state, country].filter(Boolean).join(" · ") || "Pick a country first"}</p>
      <Input className="bg-secondary/60 h-9" placeholder="Organization name (e.g. NHS Royal London Hospital)" defaultValue={q} onChange={(e) => setNewName(e.target.value)} data-testid="browse-add-name" />
      <Input className="bg-secondary/60 h-9" placeholder="Official website (optional)" value={newWebsite} onChange={(e) => setNewWebsite(e.target.value)} data-testid="browse-add-website" />
      <Input className="bg-secondary/60 h-9" placeholder="Work email domain, e.g. nhs.uk (optional)" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} data-testid="browse-add-domain" />
      <Button size="sm" className="rounded-md font-semibold w-full" disabled={!country || creating} onClick={createOrg} data-testid="browse-add-submit">{creating ? "Adding…" : "Add & select organization"}</Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setShowAdd(false); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="browse-org-dialog">
        <DialogHeader><DialogTitle>Browse organizations</DialogTitle><DialogDescription>Find an organization anywhere: Country → State/Province → Industry.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-3 gap-3">
          <Select value={country} onValueChange={(c) => { setCountry(c); setState(""); setCity(""); }}>
            <SelectTrigger className="bg-secondary/50" data-testid="browse-country"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent className="max-h-72">{countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={state} onValueChange={setState} disabled={!states.length}>
            <SelectTrigger className="bg-secondary/50" data-testid="browse-state"><SelectValue placeholder={states.length ? "State / Province" : "All regions"} /></SelectTrigger>
            <SelectContent className="max-h-72">{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input className="bg-secondary/50" placeholder="City (optional)" value={city} onChange={(e) => setCity(e.target.value)} data-testid="browse-city" disabled={!country} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setIndustry("")} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${!industry ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>All industries</button>
          {(meta?.industry_types || []).map((t) => (
            <button key={t} data-testid={`browse-industry-${t}`} onClick={() => setIndustry(industry === t ? "" : t)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${industry === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{t}</button>
          ))}
        </div>
        <Input className="bg-secondary/50" placeholder="Search organization name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="browse-search" />
        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="browse-orgs-list">
          {!country && <p className="text-sm text-muted-foreground text-center py-4">Pick a country to start.</p>}
          {country && orgs.length === 0 && (
            <div className="text-center py-4 space-y-3" data-testid="browse-empty">
              <p className="text-sm text-muted-foreground">No {industry ? `${industry.toLowerCase()} ` : ""}organizations found{state ? ` in ${state}, ` : " in "}{country} yet.</p>
              {addForm()}
            </div>
          )}
          {orgs.map((o) => (
            <button key={o.id} data-testid={`browse-org-${o.id}`} onClick={() => { onSelect(o); setOpen(false); }}
              className="w-full text-left rounded-md bg-secondary/40 hover:bg-secondary/70 px-3 py-2.5 transition-colors flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate flex items-center gap-1">{o.name}{o.verified && <SealCheck className="h-3.5 w-3.5 text-sky-400 shrink-0" weight="fill" />}</p>
                <p className="text-xs text-muted-foreground truncate">{o.type}{o.state ? ` · ${o.state}` : ""}{o.country ? ` · ${o.country}` : ""}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" weight="bold" />
            </button>
          ))}
        </div>
        {country && orgs.length > 0 && (
          showAdd ? addForm() : (
            <button onClick={() => setShowAdd(true)} data-testid="browse-add-toggle" className="text-xs text-primary hover:underline text-center w-full">Can't find it? Add your organization</button>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

const TILES = [
  { key: "regular", label: "Regular Shifts", icon: CalendarBlank, color: "text-emerald-400", tab: "available" },
  { key: "overtime", label: "Overtime", icon: Clock, color: "text-violet-300", tab: "available" },
  { key: "paid_duty", label: "Paid Duty", icon: ShieldCheck, color: "text-sky-300", tab: "available" },
  { key: "swaps", label: "Shift Swaps", icon: ArrowsClockwise, color: "text-amber-300", tab: "swaps" },
];

function SummaryTiles({ counts, activeCategory, onPick }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="summary-tiles">
      {TILES.map((t) => {
        const n = counts?.[t.key] ?? 0;
        const active = activeCategory === t.key;
        return (
          <button key={t.key} data-testid={`tile-${t.key}`} onClick={() => onPick(t)}
            className={`rounded-lg border p-4 text-left transition-all duration-200 ${active ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"}`}>
            <div className="flex items-center justify-between">
              <t.icon className={`h-5 w-5 ${t.color}`} weight="bold" />
              <span className="text-2xl font-black" data-testid={`tile-count-${t.key}`}>{n}</span>
            </div>
            <p className="text-sm font-semibold mt-2">{t.label}</p>
            <p className="text-xs text-muted-foreground">{n === 1 ? "1 open listing" : `${n} open listings`}</p>
          </button>
        );
      })}
    </div>
  );
}


export default function Marketplace() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ shift_type: "all", day_part: "all", q: "", date: "all", category: "all" });
  const [activeTab, setActiveTab] = useState("available");
  const [selectedOrg, setSelectedOrg] = useState(null);

  const { data: meta } = useQuery({ queryKey: ["marketplace-meta"], queryFn: async () => (await api.get("/marketplace/meta")).data });
  const { data: profile } = useQuery({ queryKey: ["work-profile"], queryFn: async () => (await api.get("/profile/work")).data });

  const wp = user?.work_profile || profile?.work_profile;

  const { data: orgNav } = useQuery({ queryKey: ["marketplace-orgs"], queryFn: async () => (await api.get("/profile/marketplace-orgs")).data, enabled: !!wp });
  const selectedOrgId = selectedOrg?.id || orgNav?.last_org || wp?.organization_id;

  const { data: summary } = useQuery({
    queryKey: ["org-summary", selectedOrgId],
    queryFn: async () => (await api.get(`/organizations/${selectedOrgId}/summary`)).data,
    enabled: !!selectedOrgId,
  });
  const isMember = summary ? summary.is_member : (selectedOrgId === wp?.organization_id);
  const orgInfo = summary?.organization;

  const params = new URLSearchParams();
  if (selectedOrgId) params.set("organization_id", selectedOrgId);
  if (filters.shift_type !== "all") params.set("shift_type", filters.shift_type);
  if (filters.day_part !== "all") params.set("day_part", filters.day_part);
  if (filters.category !== "all") params.set("category", filters.category);
  if (filters.q) params.set("q", filters.q);

  const { data: shifts = [], isLoading } = useQuery({
    queryKey: ["shifts", selectedOrgId, filters],
    queryFn: async () => (await api.get(`/shifts?${params.toString()}`)).data,
    enabled: !!selectedOrgId,
  });
  const { data: mine = [] } = useQuery({
    queryKey: ["my-shifts"],
    queryFn: async () => (await api.get("/shifts/mine")).data,
    enabled: !!wp,
  });
  const { data: swaps = [] } = useQuery({
    queryKey: ["swaps", selectedOrgId],
    queryFn: async () => (await api.get(`/swaps?organization_id=${selectedOrgId}`)).data,
    enabled: !!selectedOrgId,
  });
  const { data: working = [] } = useQuery({
    queryKey: ["assigned-to-me"],
    queryFn: async () => (await api.get("/shifts/assigned-to-me")).data,
    enabled: !!wp,
  });
  const { data: myRequests = [] } = useQuery({
    queryKey: ["my-requests"],
    queryFn: async () => (await api.get("/shifts/my-requests")).data,
    enabled: !!wp,
  });

  const selectOrg = async (o) => {
    setSelectedOrg({ id: o.id, name: o.name });
    setFilters((f) => ({ ...f, category: "all" }));
    try { await api.put("/profile/last-org", { organization_id: o.id }); } catch { /* noop */ }
    qc.invalidateQueries({ queryKey: ["marketplace-orgs"] });
  };
  const pickTile = (t) => { setFilters((f) => ({ ...f, category: t.key === "swaps" ? "all" : t.key })); setActiveTab(t.tab); };

  const inDateRange = (dstr) => {
    if (filters.date === "all" || !dstr) return true;
    const d = new Date(`${dstr}T00:00:00`);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = (d - today) / 86400000;
    if (filters.date === "today") return diff >= 0 && diff < 1;
    if (filters.date === "week") return diff >= 0 && diff <= 7;
    if (filters.date === "month") return diff >= 0 && diff <= 31;
    return true;
  };
  const visibleShifts = shifts.filter((s) => inDateRange(s.date));

  return (
    <div className="animate-fade-up" data-testid="marketplace-page">
      <div className="flex items-center gap-2 mb-1"><Storefront className="h-5 w-5 text-primary" weight="bold" /><p className="overline text-primary">Shift Exchange</p></div>
      <h1 className="text-3xl font-black tracking-tight">Shift Marketplace</h1>
      <p className="text-muted-foreground text-sm mt-1 mb-6">Give away, claim and manage shifts with verified colleagues in your organization.</p>

      {!wp ? (
        <WorkProfileGate meta={meta} />
      ) : (
        <>
          <Card className="p-4 mb-5 bg-card border-border" data-testid="org-selector-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Buildings className="h-5 w-5 text-primary shrink-0" weight="bold" />
                  <span className="text-lg font-black tracking-tight truncate" data-testid="selected-org-name">{orgInfo?.name || wp.organization_name}</span>
                  {orgInfo?.verified && <Badge className="bg-sky-500/15 text-sky-400 border-0 text-[10px] uppercase flex items-center gap-1" data-testid="verified-org-badge"><SealCheck className="h-3 w-3" weight="fill" /> Verified</Badge>}
                  {isMember && <Badge className="bg-emerald-500/15 text-emerald-400 border-0 text-[10px] uppercase">Member</Badge>}
                  <BrowseOrgDialog meta={meta} onSelect={selectOrg} trigger={<button className="text-xs text-primary hover:underline ml-1" data-testid="change-org-btn">Change</button>} />
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {(orgInfo?.type || wp.organization_type) || "Organization"}
                  {orgInfo?.city ? ` · ${orgInfo.city}` : ""}{(orgInfo?.state || "") ? ` · ${orgInfo.state}` : ""}{(orgInfo?.country || "") ? `, ${orgInfo.country}` : ""}
                  {summary?.member_count != null ? ` · ${summary.member_count} member${summary.member_count === 1 ? "" : "s"}` : ""}
                </p>
                {(orgNav?.recent_orgs || []).length > 1 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="recent-orgs">
                    <span className="text-[11px] text-muted-foreground self-center">Quick switch:</span>
                    {orgNav.recent_orgs.filter((r) => r.id !== selectedOrgId).slice(0, 4).map((r) => (
                      <button key={r.id} data-testid={`recent-org-${r.id}`} onClick={() => selectOrg(r)}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-secondary/60 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors">{r.name}</button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <AlertsDialog meta={meta} wp={wp} />
                <PrefsDialog prefs={profile?.marketplace_prefs || { notify_inapp: true, email_frequency: "off", shift_types: [] }} meta={meta} />
                {isMember && <PostSwapDialog wp={wp} meta={meta} />}
                {isMember && <PostShiftDialog wp={wp} meta={meta} />}
              </div>
            </div>
            {!isMember && orgInfo && (
              <div className="flex flex-wrap items-center justify-between gap-2 mt-3 rounded-md bg-amber-500/10 text-amber-300 px-3 py-2.5 text-sm" data-testid="join-banner">
                <span>You're viewing <strong>{orgInfo.name}</strong>. Join to claim or post shifts here.</span>
                <ChangeProfileDialog meta={meta} title={`Join ${orgInfo.name}`}
                  initial={{ country: orgInfo.country, state: orgInfo.state, city: orgInfo.city, industry: orgInfo.type, orgId: orgInfo.id, orgName: orgInfo.name, orgType: orgInfo.type, deptLabel: orgInfo.department_label || "Department" }}
                  onDone={() => { selectOrg(orgInfo); qc.invalidateQueries({ queryKey: ["org-summary"] }); }}
                  trigger={<Button size="sm" className="rounded-md font-semibold" data-testid="join-org-btn">Join this organization</Button>} />
              </div>
            )}
          </Card>

          <SummaryTiles counts={summary?.counts} activeCategory={activeTab === "swaps" ? "swaps" : filters.category} onPick={pickTile} />

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList data-testid="marketplace-tabs">
              <TabsTrigger value="available" data-testid="tab-available">Available Shifts</TabsTrigger>
              <TabsTrigger value="swaps" data-testid="tab-swaps">Shift Swaps</TabsTrigger>
              <TabsTrigger value="mine" data-testid="tab-mine">My Listings</TabsTrigger>
              <TabsTrigger value="requests" data-testid="tab-requests">My Requests</TabsTrigger>
            </TabsList>

            <TabsContent value="available" className="mt-5">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-5">
                <Select value={filters.shift_type} onValueChange={(v) => setFilters({ ...filters, shift_type: v })}>
                  <SelectTrigger className="w-44 bg-secondary/50" data-testid="filter-shift-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All shift types</SelectItem>
                    {(meta?.shift_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.day_part} onValueChange={(v) => setFilters({ ...filters, day_part: v })}>
                  <SelectTrigger className="w-36 bg-secondary/50" data-testid="filter-day-part"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Day & night</SelectItem>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="night">Night</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.date} onValueChange={(v) => setFilters({ ...filters, date: v })}>
                  <SelectTrigger className="w-36 bg-secondary/50" data-testid="filter-date"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All dates</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="week">Next 7 days</SelectItem>
                    <SelectItem value="month">Next 31 days</SelectItem>
                  </SelectContent>
                </Select>
                <Input className="w-56 bg-secondary/50" placeholder="Search location / notes…" data-testid="filter-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
              </div>

              {isLoading ? <p className="text-muted-foreground text-sm">Loading shifts…</p>
              : visibleShifts.length === 0 ? (
                <Card className="p-8 text-center bg-card border-border" data-testid="shifts-empty">
                  <p className="text-muted-foreground">No available shifts right now. Check back soon or post one yourself.</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shifts-grid">
                  {visibleShifts.map((s) => <ShiftCard key={s.id} s={s} />)}
                </div>
              )}

              <HowItWorks />
              <div className="flex items-center gap-2 mt-4 rounded-md bg-primary/10 text-primary px-4 py-3 text-sm" data-testid="verified-banner">
                <ShieldCheck className="h-4 w-4 shrink-0" weight="bold" />
                All users and organizations are verified to ensure a safe and trusted marketplace.
              </div>
            </TabsContent>

            <TabsContent value="mine" className="mt-5">
              {mine.length === 0 ? (
                <Card className="p-8 text-center bg-card border-border" data-testid="my-shifts-empty">
                  <p className="text-muted-foreground">You haven't posted any shifts yet.</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-shifts-grid">
                  {mine.map((s) => s.listing_type === "swap" ? <SwapListingCard key={s.id} s={s} /> : <MyListingCard key={s.id} s={s} />)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="swaps" className="mt-5">
              {swaps.length === 0 ? (
                <Card className="p-8 text-center bg-card border-border" data-testid="swaps-empty">
                  <p className="text-muted-foreground">No open swaps right now. Offer one to trade a shift.</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="swaps-grid">
                  {swaps.map((s) => <SwapCard key={s.id} s={s} />)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="requests" className="mt-5">
              {myRequests.length === 0 ? (
                <Card className="p-8 text-center bg-card border-border" data-testid="my-requests-empty">
                  <p className="text-muted-foreground">You haven't claimed any shifts yet. Browse Available Shifts and claim one.</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-requests-grid">
                  {myRequests.map((s) => <MyRequestCard key={`${s.id}-${s.my_request_status}`} s={s} />)}
                </div>
              )}

              {working.length > 0 && (
                <div className="mt-8">
                  <h3 className="font-bold mb-3">Shifts I'm working</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="working-grid">
                    {working.map((s) => (
                      <Card key={s.id} className="p-5 bg-card border-border" data-testid={`working-${s.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold">{s.shift_type}</span>
                          <Badge className={`${STATUS_STYLE[s.status] || "bg-secondary text-muted-foreground"} border-0 text-[10px] uppercase`}>{s.status}</Badge>
                          {s.estimated_earnings != null && <span className="text-xs font-bold text-emerald-400">${s.estimated_earnings}</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{s.organization_name} · {s.date} {s.start_time}{s.end_time ? `–${s.end_time}` : ""}</p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <ChatDialog shiftId={s.id} otherId={s.poster_id} testid={`working-chat-${s.id}`} />
                          {s.status === "completed" && <RateDialog shiftId={s.id} triggerLabel="Rate the poster" testid={`working-rate-${s.id}`} />}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
